import { useEffect, useRef } from 'react'

import { type AuthorPreview } from '@/entities/authors'
import {
  TrackTypeEnum as TrackType,
  TrackTypeAlbumTypeMatchEnum,
  TrackTypeTagContentTypeMatchEnum,
  getTrackTypeString,
  trackByIdQuery,
  trackToggleArchive,
  trackTypeArray,
} from '@/entities/tracks'
import {
  DraggerUploadFiles,
  type UploadFileWithId,
  UploadPicture,
} from '@/features/upload-files'
import { type AlbumModel, type TrackTypeEnum } from '@/shared/api'
import { hasFormChanged } from '@/shared/lib/form-utils'
import { CopyButton, PrimaryButton, SelfValidateInput } from '@/shared/ui'
import { Button, Checkbox, Flex, Form, Radio, Select, Spin } from 'antd'
import { useUnit } from 'effector-react'
import { type useFormik } from 'formik'

import { type TrackFormModel } from '..'
import { MAX_FIELD_LENGTH } from '../lib/constants'
import {
  $albumsTrackFormSearch,
  $authorsTrackFormSearch,
  $coauthorsTrackFormSearch,
  $moodsTrackFormSearch,
  $tagsTrackFormSearch,
  $trackFormAlbums,
  $trackFormAuthors,
  $trackFormCoauthors,
  $trackFormMoods,
  $trackFormSubmitting,
  $trackFormTags,
  albumsTrackFormAccumulatedDataReset,
  albumsTrackFormFiltersInit,
  albumsTrackFormPageIncreased,
  albumsTrackFormQuery,
  albumsTrackFormSearchChanged,
  albumsTrackFormSearchReset,
  authorsTrackFormPageIncreased,
  authorsTrackFormQuery,
  authorsTrackFormSearchChanged,
  authorsTrackFormSearchReset,
  coauthorsTrackFormPageIncreased,
  coauthorsTrackFormQuery,
  coauthorsTrackFormSearchChanged,
  coauthorsTrackFormSearchReset,
  moodsTrackFormPageIncreased,
  moodsTrackFormQuery,
  moodsTrackFormSearchChanged,
  moodsTrackFormSearchReset,
  tagsTrackFormFiltersInit,
  tagsTrackFormPageIncreased,
  tagsTrackFormQuery,
  tagsTrackFormSearchChanged,
  tagsTrackFormSearchReset,
  trackFormContentTypeChanged,
  trackFormTrackDataChanged,
} from '../model/track-form.model'

type TrackFormBaseProps = {
  isModal: boolean
  availableTrackTypes?: TrackTypeEnum[]
  initialAuthor?: AuthorPreview
} & Pick<
  ReturnType<typeof useFormik<TrackFormModel>>,
  'handleSubmit' | 'setFieldValue' | 'values' | 'initialValues'
>

export const TrackFormBase = ({
  isModal,
  availableTrackTypes,
  initialAuthor,
  handleSubmit,
  setFieldValue,
  values,
  initialValues,
}: TrackFormBaseProps) => {
  // Query
  const trackQueryStatus = useUnit(trackByIdQuery.$status)
  const { data: trackData } = useUnit(trackByIdQuery)

  const changeTrackData = useUnit(trackFormTrackDataChanged)

  const toggleArchive = useUnit(trackToggleArchive)

  useEffect(() => {
    if (!trackData) return
    changeTrackData(trackData)
  }, [trackData, changeTrackData])

  // ContentType
  const contentTypeChanged = useUnit(trackFormContentTypeChanged)

  // Authors
  const authors = useUnit(authorsTrackFormQuery)
  const authorSearchQuery = useUnit($authorsTrackFormSearch)
  const authorsChangeSearch = useUnit(authorsTrackFormSearchChanged)
  const authorsResetSearch = useUnit(authorsTrackFormSearchReset)
  const authorsPageIncreased = useUnit(authorsTrackFormPageIncreased)
  const authorsData = useUnit($trackFormAuthors)

  // Coauthors
  const coauthors = useUnit(coauthorsTrackFormQuery)
  const coauthorSearchQuery = useUnit($coauthorsTrackFormSearch)
  const coauthorsChangeSearch = useUnit(coauthorsTrackFormSearchChanged)
  const coauthorsResetSearch = useUnit(coauthorsTrackFormSearchReset)
  const coauthorsPageIncreased = useUnit(coauthorsTrackFormPageIncreased)
  const coauthorsData = useUnit($trackFormCoauthors)

  // Tags
  const tags = useUnit(tagsTrackFormQuery)
  const tagsInitFilters = useUnit(tagsTrackFormFiltersInit)
  const tagsChangeSearch = useUnit(tagsTrackFormSearchChanged)
  const tagsSearch = useUnit($tagsTrackFormSearch)
  const tagsResetSearch = useUnit(tagsTrackFormSearchReset)
  const tagsPageIncreased = useUnit(tagsTrackFormPageIncreased)
  const tagsData = useUnit($trackFormTags)

  // Moods
  const moods = useUnit(moodsTrackFormQuery)
  const moodsChangeSearch = useUnit(moodsTrackFormSearchChanged)
  const moodsSearch = useUnit($moodsTrackFormSearch)
  const moodsResetSearch = useUnit(moodsTrackFormSearchReset)
  const moodsPageIncreased = useUnit(moodsTrackFormPageIncreased)
  const moodsData = useUnit($trackFormMoods)

  // Albums
  const albums = useUnit(albumsTrackFormQuery)
  const albumsInitFilters = useUnit(albumsTrackFormFiltersInit)
  const albumsSearch = useUnit($albumsTrackFormSearch)
  const changeAlbumsSearch = useUnit(albumsTrackFormSearchChanged)
  const albumsResetSearch = useUnit(albumsTrackFormSearchReset)
  const albumsPageIncreased = useUnit(albumsTrackFormPageIncreased)
  const albumsData = useUnit($trackFormAlbums)
  const albumsResetAccumulatedData = useUnit(
    albumsTrackFormAccumulatedDataReset,
  )

  const submitting = useUnit($trackFormSubmitting)
  // Album subscription | Picture from Album
  const selectedAlbum = useRef<AlbumModel | undefined>(undefined)
  useEffect(() => {
    selectedAlbum.current = albums.data?.items.find(
      (a) => a.id === values.albumId,
    )
    if (!values.albumId) void setFieldValue('useAlbumPicture', false)
    if (selectedAlbum.current?.picture) {
      const albumPictureFile: UploadFileWithId = {
        id: selectedAlbum.current.picture.id,
        uid: selectedAlbum.current.picture.id,
        name: selectedAlbum.current.picture.name,
        status: 'done',
        url: selectedAlbum.current.picture.fullCdnUrl,
      } as UploadFileWithId
      void setFieldValue('albumPictureFile', albumPictureFile)
    }
  }, [values.albumId, albums.data, setFieldValue])

  // Type and Authors subscription
  useEffect(() => {
    const albumType = values.type
      ? TrackTypeAlbumTypeMatchEnum[values.type]
      : undefined

    if (albumType && values.authorId) {
      albumsInitFilters({ Types: [albumType], AuthorIds: [values.authorId] })
    }

    void setFieldValue('albumId', null)
  }, [
    values.type,
    initialValues.albumId,
    values.authorId,
    setFieldValue,
    albumsInitFilters,
  ])

  useEffect(() => {
    if (!values.type) return
    const tagContentType = TrackTypeTagContentTypeMatchEnum[values.type]
    if (!tagContentType) return
    tagsInitFilters({
      Types: [tagContentType],
    })
    void setFieldValue('genreIds', initialValues.genreIds)
    void setFieldValue('albumId', initialValues.albumId)
  }, [
    initialValues.genreIds,
    initialValues.albumId,
    setFieldValue,
    tagsInitFilters,
    values.type,
  ])

  useEffect(() => {
    if (!values.type) return
    contentTypeChanged(values.type)
  }, [values.type, contentTypeChanged])

  useEffect(() => {
    albumsResetAccumulatedData()
  }, [values.authorId, values.type, albumsResetAccumulatedData])

  const handleAuthorsSelectPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      authorsPageIncreased()
    }
  }

  const handleCoauthorsSelectPopupScroll = (
    e: React.UIEvent<HTMLDivElement>,
  ) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      coauthorsPageIncreased()
    }
  }

  const handleTagsSelectPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      tagsPageIncreased()
    }
  }

  const handleMoodsSelectPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      moodsPageIncreased()
    }
  }

  const handleAlbumsSelectPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      albumsPageIncreased()
    }
  }

  const formControlButtonsDisabled =
    !hasFormChanged(values, initialValues, ['albumPictureFile']) || submitting

  return (
    <div style={{ maxWidth: 600 }}>
      {isModal && trackData && (
        <Form.Item style={{ marginTop: 24 }}>
          <Button
            variant="solid"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              borderColor: 'rgba(0, 0, 0, 0.06)',
            }}
            loading={toggleArchive.pending}
            onClick={() => {
              void toggleArchive.start({
                id: trackData.id,
                data: { isArchive: !trackData.isArchive },
              })
            }}
          >
            {trackData.isArchive ? 'Раза' : 'А'}рхивировать запись
          </Button>
        </Form.Item>
      )}
      <Form.Item label="Название" layout="vertical" required>
        <SelfValidateInput
          showCount
          maxLength={MAX_FIELD_LENGTH.name}
          value={values.name}
          onChange={(value) => {
            void setFieldValue('name', value)
          }}
        />
      </Form.Item>

      <Form.Item
        label="Автор"
        required
        style={{ marginBottom: '8px' }}
        layout="vertical"
      >
        <Flex gap={4}>
          <Select
            showSearch
            loading={authors.pending}
            searchValue={authorSearchQuery || ''}
            optionFilterProp="label"
            disabled={
              typeof initialAuthor?.id === 'string' &&
              values.authorId === initialAuthor.id
            }
            value={values.authorId}
            onChange={(value: string) => {
              void setFieldValue('authorId', value)
              authorsResetSearch()
            }}
            onSearch={(value: string) => {
              authorsChangeSearch(value)
            }}
            notFoundContent={authors.pending ? <Spin size="small" /> : null}
            onOpenChange={(open) => {
              if (!open) {
                authorsResetSearch()
              }
            }}
            onPopupScroll={handleAuthorsSelectPopupScroll}
          >
            {authorsData
              .filter((el) => !values.coauthorIds?.includes(el.id))
              .map((el) => (
                <Select.Option key={el.id} value={el.id} label={el.nickname}>
                  {el.nickname}
                </Select.Option>
              ))}
          </Select>
          <CopyButton
            getText={() => {
              const selected = authorsData.find(
                (el) => el.id === values.authorId,
              )
              return selected?.nickname ?? null
            }}
          />
        </Flex>
      </Form.Item>
      {values.authorId && (
        <Form.Item
          label="Соавторы"
          layout="vertical"
          style={{ marginBottom: '8px' }}
        >
          <Flex gap={4}>
            <Select
              mode="multiple"
              showSearch
              value={values.coauthorIds}
              searchValue={coauthorSearchQuery || ''}
              loading={coauthors.pending}
              optionFilterProp="label"
              onChange={(value: string[]) => {
                void setFieldValue('coauthorIds', value)
                coauthorsResetSearch()
              }}
              onSearch={(value: string) => {
                coauthorsChangeSearch(value)
              }}
              notFoundContent={authors.pending ? <Spin size="small" /> : null}
              onOpenChange={(open) => {
                if (!open) {
                  coauthorsResetSearch()
                }
              }}
              onPopupScroll={handleCoauthorsSelectPopupScroll}
            >
              {coauthorsData
                .filter((el) => el.id !== values.authorId)
                .map((el) => (
                  <Select.Option key={el.id} value={el.id} label={el.nickname}>
                    {el.nickname}
                  </Select.Option>
                ))}
            </Select>
            <CopyButton
              getText={() => {
                const ids = values.coauthorIds
                if (!ids || ids.length === 0) return null
                const selected = ids
                  .map(
                    (id) => coauthorsData.find((el) => el.id === id)?.nickname,
                  )
                  .filter((name): name is string => Boolean(name))
                if (selected.length === 0) return null
                return selected.join(', ')
              }}
            />
          </Flex>
        </Form.Item>
      )}

      <Form.Item label="Тип контента" layout="vertical" required>
        <Radio.Group
          disabled={trackQueryStatus !== 'initial'}
          value={values.type}
          options={trackTypeArray
            .filter((tracktype) => {
              if (tracktype === TrackType.Jingle) return false
              if (
                Array.isArray(availableTrackTypes) &&
                availableTrackTypes.length > 0
              ) {
                return availableTrackTypes.includes(tracktype)
              }
              return true
            })
            .map((tracktype) => ({
              value: tracktype,
              label: getTrackTypeString(tracktype),
            }))}
          onChange={(value) => {
            const newType: TrackTypeEnum = value.target.value
            void setFieldValue('type', newType)
          }}
        />
      </Form.Item>

      {values.type !== TrackType.Sermon ? (
        <>
          <Form.Item label="Жанр" layout="vertical" required>
            <Flex gap={4}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                onSearch={(value: string) => {
                  tagsChangeSearch(value)
                }}
                optionFilterProp="label"
                loading={tags.pending}
                value={values.genreIds}
                searchValue={tagsSearch || ''}
                onChange={(value: number[]) => {
                  void setFieldValue('genreIds', value)
                  tagsResetSearch()
                }}
                onOpenChange={(open) => {
                  if (!open) {
                    tagsResetSearch()
                  }
                }}
                onPopupScroll={handleTagsSelectPopupScroll}
              >
                {tagsData.map((el) => (
                  <Select.Option key={el.id} value={el.id} label={el.name}>
                    {el.name}
                  </Select.Option>
                ))}
              </Select>
              <CopyButton
                getText={() => {
                  const ids = values.genreIds
                  if (!ids || ids.length === 0) return null
                  const selected = ids
                    .map((id) => tagsData.find((el) => el.id === id)?.name)
                    .filter((name): name is string => Boolean(name))
                  if (selected.length === 0) return null
                  return selected.join(', ')
                }}
              />
            </Flex>
          </Form.Item>
        </>
      ) : null}

      {values.type !== TrackType.Sermon ? (
        <>
          <Form.Item label="Тег настроения" layout="vertical">
            <Flex gap={4}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                onSearch={(value: string) => {
                  moodsChangeSearch(value)
                }}
                optionFilterProp="label"
                loading={moods.pending}
                value={values.moodIds}
                searchValue={moodsSearch || ''}
                onChange={(value: number[]) => {
                  void setFieldValue('moodIds', value)
                  moodsResetSearch()
                }}
                onOpenChange={(open) => {
                  if (!open) {
                    moodsResetSearch()
                  }
                }}
                onPopupScroll={handleMoodsSelectPopupScroll}
              >
                {moodsData.map((el) => (
                  <Select.Option key={el.id} value={el.id} label={el.name}>
                    {el.name}
                  </Select.Option>
                ))}
              </Select>
              <CopyButton
                getText={() => {
                  const ids = values.moodIds
                  if (!ids || ids.length === 0) return null
                  const selected = ids
                    .map((id) => moodsData.find((el) => el.id === id)?.name)
                    .filter((name): name is string => Boolean(name))
                  if (selected.length === 0) return null
                  return selected.join(', ')
                }}
              />
            </Flex>
          </Form.Item>
        </>
      ) : null}

      <Form.Item
        label="Альбом"
        layout="vertical"
        required={
          values.type === TrackType.Music || values.type === TrackType.Podcast
        }
      >
        <Flex gap={4}>
          <Select
            showSearch
            style={{ width: '100%' }}
            onSearch={(value: string) => {
              if (values.authorId) {
                changeAlbumsSearch(value)
              }
            }}
            optionFilterProp="label"
            loading={albums.pending}
            value={values.albumId}
            searchValue={albumsSearch || ''}
            onChange={(value: string) => {
              void setFieldValue('albumId', value)
              albumsResetSearch()
            }}
            onPopupScroll={handleAlbumsSelectPopupScroll}
          >
            {albumsData.map((el) => (
              <Select.Option
                key={el.albumId}
                value={el.albumId}
                label={el.name}
              >
                {el.name}
              </Select.Option>
            ))}
          </Select>
          <CopyButton
            getText={() => {
              const selected = albumsData.find(
                (el) => el.albumId === values.albumId,
              )
              return selected?.name ?? null
            }}
          />
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          values.albumId && values.useAlbumPicture
            ? 'Обложка альбома'
            : 'Обложка'
        }
        layout="vertical"
        required
      >
        <UploadPicture
          value={
            (values.albumId && values.useAlbumPicture
              ? values.albumPictureFile
              : values.pictureFile) ?? undefined
          }
          onChange={(fileList) => {
            const file = fileList[0] ?? null
            if (values.useAlbumPicture) return
            void setFieldValue('pictureFile', file)
          }}
          onReset={() => {
            void setFieldValue('pictureFile', null)
          }}
          showRemoveIcon={!values.useAlbumPicture}
          showUploadButton={!values.useAlbumPicture}
        />
      </Form.Item>

      {values.albumId ? (
        <Form.Item layout="vertical">
          <Checkbox
            checked={values.useAlbumPicture}
            onChange={(value) => {
              const newUseAlbumPicture = value.target.checked
              void setFieldValue('useAlbumPicture', newUseAlbumPicture)
            }}
          >
            Использовать обложку альбома
          </Checkbox>
        </Form.Item>
      ) : null}

      <Form.Item label="Загрузка треков" required layout="vertical">
        <DraggerUploadFiles
          maxCount={1}
          accept=".mp3"
          value={values.trackFile ? [values.trackFile] : null}
          onChange={(fileList) => {
            const file = fileList[0] || null
            if (!file) return
            void setFieldValue('trackFile', file)
          }}
        />
      </Form.Item>

      <PrimaryButton
        disabled={formControlButtonsDisabled}
        loading={submitting}
        onClick={() => {
          handleSubmit()
        }}
      >
        {trackQueryStatus === 'initial'
          ? 'Создать трек'
          : 'Сохранить изменения'}
      </PrimaryButton>
    </div>
  )
}
